/**
 * BigSplice Chat State Hook
 * Manages chat state, sessions, and message handling
 */

import { useReducer, useEffect, useCallback, useMemo } from 'react';
import { lambdaService } from '../services/lambdaService';

// Type definitions (moved from types/chat.ts for self-containment)
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
  metadata?: {
    resultType?: string;
    resultData?: unknown;
    processingLayer?: string;
    executionTime?: number;
    error?: string;
  };
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

export interface DownloadProgress {
  status: 'pending' | 'downloading' | 'processing' | 'completed' | 'failed';
  percentage: number;
  speed?: string;
  eta?: string;
  filename?: string;
  error?: string;
}

interface ChatState {
  sessions: ChatSession[];
  currentSessionId: string | null;
  isLoading: boolean;
  error: string | null;
  selectedFile: string | null;
  selectedFileKey: string | null;
  deleteConfirmation: {
    step: 'button' | 'text' | 'confirmed';
    targetFile: string | null;
    targetType: 'file' | 'folder';
    typedText: string;
  };
  uploadHelper: {
    isOpen: boolean;
    method: 'direct' | 'url' | 's3' | 'sql' | null;
    downloadProgress: DownloadProgress | null;
  };
}

type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SESSIONS'; payload: ChatSession[] }
  | { type: 'SET_CURRENT_SESSION'; payload: string | null }
  | { type: 'ADD_MESSAGE'; payload: { sessionId: string; message: ChatMessage } }
  | { type: 'UPDATE_MESSAGE'; payload: { sessionId: string; messageId: string; updates: Partial<ChatMessage> } }
  | { type: 'SET_SELECTED_FILE'; payload: { name: string | null; key: string | null } }
  | { type: 'SET_DELETE_CONFIRMATION'; payload: Partial<ChatState['deleteConfirmation']> }
  | { type: 'RESET_DELETE_CONFIRMATION' }
  | { type: 'SET_UPLOAD_HELPER'; payload: Partial<ChatState['uploadHelper']> }
  | { type: 'CREATE_SESSION'; payload: ChatSession }
  | { type: 'DELETE_SESSION'; payload: string };

// Session storage helpers
const SESSIONS_STORAGE_KEY = 'bigsplice-chat-sessions';
const MAX_SESSIONS = 50;

function getSessions(): ChatSession[] {
  try {
    const stored = localStorage.getItem(SESSIONS_STORAGE_KEY);
    if (!stored) return [];
    const sessions = JSON.parse(stored);
    return sessions.map((s: ChatSession) => ({
      ...s,
      createdAt: new Date(s.createdAt),
      updatedAt: new Date(s.updatedAt),
      messages: s.messages.map((m: ChatMessage) => ({
        ...m,
        timestamp: new Date(m.timestamp),
      })),
    }));
  } catch (error) {
    console.error('Failed to load chat sessions:', error);
    return [];
  }
}

function saveSession(session: ChatSession): void {
  try {
    const sessions = getSessions();
    const existingIndex = sessions.findIndex((s) => s.id === session.id);
    if (existingIndex >= 0) {
      sessions[existingIndex] = session;
    } else {
      sessions.unshift(session);
    }
    const trimmedSessions = sessions.slice(0, MAX_SESSIONS);
    localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(trimmedSessions));
  } catch (error) {
    console.error('Failed to save chat session:', error);
  }
}

function deleteSessionFromStorage(sessionId: string): void {
  try {
    const sessions = getSessions().filter((s) => s.id !== sessionId);
    localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
  } catch (error) {
    console.error('Failed to delete chat session:', error);
  }
}

function createSession(title?: string): ChatSession {
  const now = new Date();
  return {
    id: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    title: title || `Chat ${now.toLocaleDateString()}`,
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
}

function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Initial state
const initialState: ChatState = {
  sessions: [],
  currentSessionId: null,
  isLoading: false,
  error: null,
  selectedFile: null,
  selectedFileKey: null,
  deleteConfirmation: {
    step: 'button',
    targetFile: null,
    targetType: 'file',
    typedText: '',
  },
  uploadHelper: {
    isOpen: false,
    method: null,
    downloadProgress: null,
  },
};

// Reducer function
function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_SESSIONS':
      return { ...state, sessions: action.payload };

    case 'SET_CURRENT_SESSION':
      return { ...state, currentSessionId: action.payload };

    case 'ADD_MESSAGE': {
      const { sessionId, message } = action.payload;
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === sessionId
            ? {
                ...session,
                messages: [...session.messages, message],
                updatedAt: new Date(),
              }
            : session
        ),
      };
    }

    case 'UPDATE_MESSAGE': {
      const { sessionId, messageId, updates } = action.payload;
      return {
        ...state,
        sessions: state.sessions.map((session) =>
          session.id === sessionId
            ? {
                ...session,
                messages: session.messages.map((msg) =>
                  msg.id === messageId ? { ...msg, ...updates } : msg
                ),
                updatedAt: new Date(),
              }
            : session
        ),
      };
    }

    case 'SET_SELECTED_FILE':
      return {
        ...state,
        selectedFile: action.payload.name,
        selectedFileKey: action.payload.key,
      };

    case 'SET_DELETE_CONFIRMATION':
      return {
        ...state,
        deleteConfirmation: { ...state.deleteConfirmation, ...action.payload },
      };

    case 'RESET_DELETE_CONFIRMATION':
      return {
        ...state,
        deleteConfirmation: {
          step: 'button',
          targetFile: null,
          targetType: 'file',
          typedText: '',
        },
      };

    case 'SET_UPLOAD_HELPER':
      return {
        ...state,
        uploadHelper: { ...state.uploadHelper, ...action.payload },
      };

    case 'CREATE_SESSION':
      return {
        ...state,
        sessions: [action.payload, ...state.sessions],
        currentSessionId: action.payload.id,
      };

    case 'DELETE_SESSION':
      return {
        ...state,
        sessions: state.sessions.filter((s) => s.id !== action.payload),
        currentSessionId:
          state.currentSessionId === action.payload
            ? state.sessions[0]?.id || null
            : state.currentSessionId,
      };

    default:
      return state;
  }
}

export function useChatState() {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Get current session
  const currentSession = useMemo(
    () => state.sessions.find((s) => s.id === state.currentSessionId) || null,
    [state.sessions, state.currentSessionId]
  );

  // Load sessions from localStorage on mount
  useEffect(() => {
    const sessions = getSessions();
    dispatch({ type: 'SET_SESSIONS', payload: sessions });

    // Set current session to most recent or create new one
    if (sessions.length > 0) {
      dispatch({ type: 'SET_CURRENT_SESSION', payload: sessions[0].id });
    } else {
      const newSession = createSession();
      dispatch({ type: 'CREATE_SESSION', payload: newSession });
    }
  }, []);

  // Save current session to localStorage when messages change
  useEffect(() => {
    if (currentSession && currentSession.messages.length > 0) {
      saveSession(currentSession);
    }
  }, [currentSession]);

  // Create a new chat session
  const createNewSession = useCallback((title?: string) => {
    const session = createSession(title);
    dispatch({ type: 'CREATE_SESSION', payload: session });
    saveSession(session);
    return session;
  }, []);

  // Switch to a different session
  const switchSession = useCallback((sessionId: string) => {
    dispatch({ type: 'SET_CURRENT_SESSION', payload: sessionId });
  }, []);

  // Delete a session
  const deleteSession = useCallback((sessionId: string) => {
    deleteSessionFromStorage(sessionId);
    dispatch({ type: 'DELETE_SESSION', payload: sessionId });
  }, []);

  // Add a message to the current session
  const addMessage = useCallback(
    (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
      if (!state.currentSessionId) return null;

      const fullMessage: ChatMessage = {
        ...message,
        id: generateMessageId(),
        timestamp: new Date(),
      };

      dispatch({
        type: 'ADD_MESSAGE',
        payload: { sessionId: state.currentSessionId, message: fullMessage },
      });

      return fullMessage;
    },
    [state.currentSessionId]
  );

  // Update a message in the current session
  const updateMessage = useCallback(
    (messageId: string, updates: Partial<ChatMessage>) => {
      if (!state.currentSessionId) return;

      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { sessionId: state.currentSessionId, messageId, updates },
      });
    },
    [state.currentSessionId]
  );

  // Send a message and get response
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || !state.currentSessionId) return;

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Add user message
      const userMessage = addMessage({
        role: 'user',
        content: content.trim(),
        status: 'sent',
      });

      if (!userMessage) return;

      // Add placeholder assistant message
      const assistantMessage = addMessage({
        role: 'assistant',
        content: '',
        status: 'sending',
      });

      if (!assistantMessage) return;

      try {
        // Build context from recent messages
        const recentMessages = currentSession?.messages.slice(-10).map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const response = await lambdaService.sendChatMessage({
          message: content.trim(),
          sessionId: state.currentSessionId,
          selectedFile: state.selectedFile || undefined,
          selectedFileKey: state.selectedFileKey || undefined,
          context: {
            recentMessages,
          },
        });

        // Update assistant message with response
        updateMessage(assistantMessage.id, {
          content: response.message || '',
          status: response.success ? 'sent' : 'error',
          metadata: {
            resultType: response.resultType,
            resultData: response.resultData as any,
            processingLayer: response.processingLayer,
            executionTime: response.executionTime,
            error: response.error,
          },
        });

        // Handle special response types
        if (response.resultType === 'confirmation' && response.resultData) {
          const confirmData = response.resultData as any;
          dispatch({
            type: 'SET_DELETE_CONFIRMATION',
            payload: {
              step: confirmData.confirmationStep || 'button',
              targetFile: confirmData.target,
              targetType: confirmData.targetType || 'file',
            },
          });
        }

        if (response.resultType === 'upload_guidance' && response.resultData) {
          const uploadData = response.resultData as any;
          dispatch({
            type: 'SET_UPLOAD_HELPER',
            payload: {
              isOpen: uploadData.showModal || false,
              method: uploadData.method || null,
            },
          });
        }
        // Session saving is now handled by the useEffect that watches currentSession
      } catch (error) {
        updateMessage(assistantMessage.id, {
          content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          status: 'error',
          metadata: {
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [
      state.currentSessionId,
      state.selectedFile,
      state.selectedFileKey,
      state.sessions,
      currentSession,
      addMessage,
      updateMessage,
    ]
  );

  // Select a file for analysis
  const selectFile = useCallback((name: string | null, key: string | null) => {
    dispatch({ type: 'SET_SELECTED_FILE', payload: { name, key } });
  }, []);

  // Delete confirmation handlers
  const confirmDeleteStep1 = useCallback(() => {
    dispatch({ type: 'SET_DELETE_CONFIRMATION', payload: { step: 'text' } });
  }, []);

  const confirmDeleteStep2 = useCallback(
    async (typedText: string) => {
      if (typedText.toLowerCase() !== 'delete') return false;

      dispatch({
        type: 'SET_DELETE_CONFIRMATION',
        payload: { step: 'confirmed', typedText },
      });

      // Send the confirmed delete command
      if (state.deleteConfirmation.targetFile) {
        await sendMessage(`confirm delete ${state.deleteConfirmation.targetFile}`);
      }

      dispatch({ type: 'RESET_DELETE_CONFIRMATION' });
      return true;
    },
    [state.deleteConfirmation.targetFile, sendMessage]
  );

  const cancelDelete = useCallback(() => {
    dispatch({ type: 'RESET_DELETE_CONFIRMATION' });
  }, []);

  const updateDeleteTypedText = useCallback((text: string) => {
    dispatch({ type: 'SET_DELETE_CONFIRMATION', payload: { typedText: text } });
  }, []);

  // Upload helper handlers
  const openUploadHelper = useCallback((method?: 'direct' | 'url' | 's3' | 'sql') => {
    dispatch({
      type: 'SET_UPLOAD_HELPER',
      payload: { isOpen: true, method: method || null },
    });
  }, []);

  const closeUploadHelper = useCallback(() => {
    dispatch({
      type: 'SET_UPLOAD_HELPER',
      payload: { isOpen: false, method: null, downloadProgress: null },
    });
  }, []);

  const setUploadMethod = useCallback((method: 'direct' | 'url' | 's3' | 'sql') => {
    dispatch({ type: 'SET_UPLOAD_HELPER', payload: { method } });
  }, []);

  const updateDownloadProgress = useCallback((progress: DownloadProgress | null) => {
    dispatch({ type: 'SET_UPLOAD_HELPER', payload: { downloadProgress: progress } });
  }, []);

  return {
    // State
    sessions: state.sessions,
    currentSession,
    currentSessionId: state.currentSessionId,
    isLoading: state.isLoading,
    error: state.error,
    selectedFile: state.selectedFile,
    selectedFileKey: state.selectedFileKey,
    deleteConfirmation: state.deleteConfirmation,
    uploadHelper: state.uploadHelper,

    // Session actions
    createNewSession,
    switchSession,
    deleteSession,

    // Message actions
    sendMessage,
    addMessage,
    updateMessage,

    // File selection
    selectFile,

    // Delete confirmation
    confirmDeleteStep1,
    confirmDeleteStep2,
    cancelDelete,
    updateDeleteTypedText,

    // Upload helper
    openUploadHelper,
    closeUploadHelper,
    setUploadMethod,
    updateDownloadProgress,
  };
}

export type UseChatStateReturn = ReturnType<typeof useChatState>;
